import { useState, useCallback, useEffect, useRef } from 'react';
import { login as apiLogin, verifyToken } from '../api/auth';
import { getErrorMessage } from '../api/client';
import { AUTH_TOKEN_STORAGE_KEY } from '../config';

function parseJwtExpiryMs(token) {
  try {
    const payloadPart = token.split('.')[1];
    if (!payloadPart) return null;
    const normalized = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    const payload = JSON.parse(window.atob(padded));
    if (!payload.exp) return null;
    return payload.exp * 1000;
  } catch (_err) {
    return null;
  }
}

/**
 * Auth hook: token, login, logout, session verify and expiry handling.
 */
export function useAuth(options = {}) {
  const { onLoginSuccess, onLogout, onVerifySuccess } = options;
  const [token, setTokenState] = useState(
    () => sessionStorage.getItem(AUTH_TOKEN_STORAGE_KEY) || ''
  );
  const [isVerifying, setIsVerifying] = useState(!!token);
  const skipVerifyOnceRef = useRef(false);

  const setToken = useCallback(
    (newToken) => {
      if (newToken) {
        sessionStorage.setItem(AUTH_TOKEN_STORAGE_KEY, newToken);
      } else {
        sessionStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
      }
      setTokenState(newToken || '');
    },
    []
  );

  const logout = useCallback(
    (message = '') => {
      setToken('');
      if (onLogout) onLogout(message);
    },
    [setToken, onLogout]
  );

  const login = useCallback(
    async (username, password) => {
      const response = await apiLogin(username, password);
      const newToken = response?.token || '';
      if (!newToken) {
        throw new Error('Login failed. No token received.');
      }
      skipVerifyOnceRef.current = true;
      setToken(newToken);
      if (onLoginSuccess) onLoginSuccess();
      return response;
    },
    [setToken, onLoginSuccess]
  );

  // Verify token on mount when we have a token (skip right after login to avoid race)
  useEffect(() => {
    if (!token) {
      setIsVerifying(false);
      return undefined;
    }
    if (skipVerifyOnceRef.current) {
      skipVerifyOnceRef.current = false;
      setIsVerifying(false);
      if (onVerifySuccess) onVerifySuccess();
      return undefined;
    }
    let cancelled = false;
    setIsVerifying(true);
    verifyToken(token)
      .then(() => {
        if (!cancelled) {
          setIsVerifying(false);
          if (onVerifySuccess) onVerifySuccess();
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setIsVerifying(false);
          const status = err?.response?.status;
          if (status === 401) {
            setToken('');
            if (onLogout) onLogout('Session expired. Please log in again.');
          } else {
            if (onVerifySuccess) onVerifySuccess();
          }
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, onLogout, onVerifySuccess, setToken]);

  // Schedule logout at JWT expiry
  useEffect(() => {
    if (!token) return undefined;
    const expiryMs = parseJwtExpiryMs(token);
    if (!expiryMs) return undefined;
    const remainingMs = expiryMs - Date.now();
    if (remainingMs <= 0) {
      logout('Session expired. Please log in again.');
      return undefined;
    }
    const timeoutId = setTimeout(() => {
      logout('Session expired. Please log in again.');
    }, remainingMs);
    return () => clearTimeout(timeoutId);
  }, [token, logout]);

  return {
    token,
    setToken,
    login,
    logout,
    isAuthenticated: !!token,
    isVerifying,
  };
}

export { getErrorMessage as getAuthErrorMessage };
